<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { api, extractError } from '@/api/client'
import QrModal from '@/components/keys/QrModal.vue'
import { useUserStore } from '@/stores/user'
import type { UserSettings } from '@/types'

const userStore = useUserStore()

const settings = ref<UserSettings | null>(null)
const loading = ref(true)
const saving = ref(false)
const error = ref('')
const success = ref('')

const language = ref('ru')

const tfaStep = ref<'idle' | 'setup' | 'backup'>('idle')
const tfaCode = ref('')
const tfaSecret = ref('')
const tfaOtpauth = ref('')
const backupCodes = ref<string[]>([])
const qrOpen = ref(false)

const twoFaEnabled = computed(() => !!settings.value?.two_factor_enabled)
const passphraseEnabled = computed(() => !!settings.value?.passphrase_enabled)

const hasRealEmail = computed(() => !!settings.value?.has_real_email)
const hasTelegram = computed(() => !!settings.value?.has_telegram)
const authEmailEnabled = computed(() => !!settings.value?.auth_email_enabled)
const authTelegramEnabled = computed(() => !!settings.value?.auth_telegram_enabled)

// Привязка email (для аккаунтов только с Telegram)
const linkEmailOpen = ref(false)
const linkEmailValue = ref('')
const linkEmailCode = ref('')
const linkEmailSent = ref(false)
const linkBusy = ref(false)

// Привязка Telegram (для аккаунтов только с email)
const linkTgOpen = ref(false)
const linkTgCode = ref('')

// Кодовое слово
const passStep = ref<'idle' | 'set'>('idle')
const passValue = ref('')
const passTotp = ref('')
const passBusy = ref(false)

async function load() {
  loading.value = true
  error.value = ''
  try {
    const { data } = await api.get<{ success: boolean; settings: UserSettings }>(
      '/api/user/settings',
    )
    if (data.success) {
      settings.value = data.settings
      language.value = data.settings.language || 'ru'
    }
  } catch (e) {
    error.value = extractError(e)
  } finally {
    loading.value = false
  }
}

async function save() {
  saving.value = true
  error.value = ''
  success.value = ''
  try {
    const { data } = await api.post<{ success: boolean; message?: string }>(
      '/api/user/settings',
      { language: language.value },
    )
    if (data.success) {
      success.value = 'Настройки сохранены'
      await userStore.fetch(true)
    } else {
      error.value = data.message || 'Ошибка сохранения'
    }
  } catch (e) {
    error.value = extractError(e)
  } finally {
    saving.value = false
  }
}

async function start2fa() {
  error.value = ''
  try {
    const { data } = await api.post<{
      success: boolean
      secret?: string
      otpauth?: string
      message?: string
    }>('/api/user/2fa', { action: 'start' })
    if (data.success && data.otpauth) {
      tfaSecret.value = data.secret || ''
      tfaOtpauth.value = data.otpauth
      tfaStep.value = 'setup'
    } else {
      error.value = data.message || 'Не удалось начать настройку 2FA'
    }
  } catch (e) {
    error.value = extractError(e)
  }
}

async function confirm2fa() {
  error.value = ''
  try {
    const { data } = await api.post<{
      success: boolean
      backup_codes?: string[]
      message?: string
    }>('/api/user/2fa', { action: 'confirm', code: tfaCode.value.trim() })
    if (data.success) {
      backupCodes.value = data.backup_codes || []
      tfaStep.value = 'backup'
      tfaCode.value = ''
      success.value = '2FA включена'
      await load()
    } else {
      error.value = data.message || 'Неверный код'
    }
  } catch (e) {
    error.value = extractError(e)
  }
}

async function disable2fa() {
  if (!confirm('Отключить 2FA?')) return
  error.value = ''
  try {
    const { data } = await api.post<{ success: boolean }>('/api/user/2fa', { action: 'disable' })
    if (data.success) {
      success.value = '2FA отключена'
      tfaStep.value = 'idle'
      await load()
    }
  } catch (e) {
    error.value = extractError(e)
  }
}

function startPassphrase() {
  error.value = ''
  passValue.value = ''
  passTotp.value = ''
  passStep.value = 'set'
}

async function savePassphrase() {
  error.value = ''
  success.value = ''
  if (passValue.value.trim().length < 4) {
    error.value = 'Кодовое слово должно содержать минимум 4 символа'
    return
  }
  passBusy.value = true
  try {
    const payload: Record<string, unknown> = {
      action: 'change_passphrase',
      new_passphrase: passValue.value.trim(),
    }
    if (twoFaEnabled.value && passTotp.value.trim()) {
      payload.totp_code = passTotp.value.trim()
    }
    const { data } = await api.post<{ success: boolean; message?: string }>(
      '/api/user/settings',
      payload,
    )
    if (data.success) {
      success.value = passphraseEnabled.value ? 'Кодовое слово изменено' : 'Кодовое слово включено'
      passStep.value = 'idle'
      passValue.value = ''
      passTotp.value = ''
      await load()
    } else {
      error.value = data.message || 'Не удалось сохранить кодовое слово'
    }
  } catch (e) {
    error.value = extractError(e)
  } finally {
    passBusy.value = false
  }
}

async function disablePassphrase() {
  if (!confirm('Отключить кодовое слово?')) return
  error.value = ''
  success.value = ''
  const payload: Record<string, unknown> = { action: 'disable_passphrase' }
  if (twoFaEnabled.value) {
    const code = prompt('Введите код 2FA для подтверждения:') || ''
    if (!code.trim()) return
    payload.totp_code = code.trim()
  }
  passBusy.value = true
  try {
    const { data } = await api.post<{ success: boolean; message?: string }>(
      '/api/user/settings',
      payload,
    )
    if (data.success) {
      success.value = 'Кодовое слово отключено'
      await load()
    } else {
      error.value = data.message || 'Не удалось отключить кодовое слово'
    }
  } catch (e) {
    error.value = extractError(e)
  } finally {
    passBusy.value = false
  }
}

async function toggleAuthMethod(method: 'email' | 'telegram', enabled: boolean) {
  error.value = ''
  success.value = ''
  // Включение непривязанного способа -> открыть флоу привязки
  if (enabled && method === 'email' && !hasRealEmail.value) {
    linkEmailOpen.value = true
    return
  }
  if (enabled && method === 'telegram' && !hasTelegram.value) {
    linkTgOpen.value = true
    return
  }
  linkBusy.value = true
  try {
    const { data } = await api.post<{ success: boolean; message?: string; need_link?: boolean }>(
      '/api/user/settings',
      { action: 'toggle_auth_method', method, enabled },
    )
    if (data.success) {
      success.value = 'Настройки авторизации обновлены'
      await load()
    } else {
      if (data.need_link) {
        if (method === 'telegram') linkTgOpen.value = true
        else linkEmailOpen.value = true
      }
      error.value = data.message || 'Ошибка'
    }
  } catch (e) {
    error.value = extractError(e)
  } finally {
    linkBusy.value = false
  }
}

async function sendLinkEmailOtp() {
  error.value = ''
  success.value = ''
  const email = linkEmailValue.value.trim()
  if (!email) {
    error.value = 'Укажите email'
    return
  }
  linkBusy.value = true
  try {
    const { data } = await api.post<{ success: boolean; message?: string }>(
      '/api/user/settings',
      { action: 'send_link_email_otp', email },
    )
    if (data.success) {
      linkEmailSent.value = true
      success.value = 'Код отправлен на ' + email
    } else {
      error.value = data.message || 'Не удалось отправить код'
    }
  } catch (e) {
    error.value = extractError(e)
  } finally {
    linkBusy.value = false
  }
}

async function verifyLinkEmail() {
  error.value = ''
  success.value = ''
  const email = linkEmailValue.value.trim()
  const code = linkEmailCode.value.trim()
  if (!email || !code) {
    error.value = 'Введите email и код'
    return
  }
  linkBusy.value = true
  try {
    const { data } = await api.post<{ success: boolean; message?: string }>(
      '/api/user/settings',
      { action: 'link_email_verify', email, code },
    )
    if (data.success) {
      success.value = 'Email привязан'
      linkEmailOpen.value = false
      linkEmailSent.value = false
      linkEmailValue.value = ''
      linkEmailCode.value = ''
      await load()
    } else {
      error.value = data.message || 'Код не подошёл'
    }
  } catch (e) {
    error.value = extractError(e)
  } finally {
    linkBusy.value = false
  }
}

async function verifyLinkTelegram() {
  error.value = ''
  success.value = ''
  const code = linkTgCode.value.trim()
  if (!code) {
    error.value = 'Введите код из Telegram'
    return
  }
  linkBusy.value = true
  try {
    const { data } = await api.post<{ success: boolean; message?: string }>(
      '/api/user/settings',
      { action: 'link_telegram_verify', code },
    )
    if (data.success) {
      success.value = 'Telegram привязан'
      linkTgOpen.value = false
      linkTgCode.value = ''
      await load()
    } else {
      error.value = data.message || 'Код не подошёл'
    }
  } catch (e) {
    error.value = extractError(e)
  } finally {
    linkBusy.value = false
  }
}

onMounted(() => void load())
</script>

<template>
  <div class="mx-auto max-w-2xl">
    <h1 class="page-title mb-6">Настройки</h1>

    <div v-if="loading" class="panel text-muted">Загрузка…</div>

    <div v-else class="space-y-4">
      <div v-if="error" class="alert alert-danger">{{ error }}</div>
      <div v-if="success" class="alert alert-success">{{ success }}</div>

      <div class="panel space-y-4">
        <h2 class="font-bold">Язык</h2>
        <select v-model="language" class="form-select">
          <option value="ru">Русский</option>
          <option value="en">English</option>
          <option value="fr">Français</option>
          <option value="de">Deutsch</option>
          <option value="tr">Türkçe</option>
          <option value="ar">العربية</option>
          <option value="zh">中文</option>
        </select>
      </div>

      <div class="panel space-y-4">
        <h2 class="font-bold">Способы входа</h2>
        <p class="text-sm text-muted">
          Должен быть включён хотя бы один способ. Привяжите второй, чтобы не потерять доступ.
        </p>

        <!-- Email -->
        <div class="rounded-lg border border-white-light/60 p-3">
          <div class="flex items-center justify-between gap-3">
            <div>
              <div class="font-semibold">Вход по Email</div>
              <div class="text-xs text-muted">
                {{ hasRealEmail ? settings?.email : 'Email не привязан' }}
              </div>
            </div>
            <label v-if="hasRealEmail" class="relative inline-flex cursor-pointer items-center">
              <input
                type="checkbox"
                class="peer sr-only"
                :checked="authEmailEnabled"
                :disabled="linkBusy"
                @change="toggleAuthMethod('email', !authEmailEnabled)"
              />
              <span class="h-6 w-11 rounded-full bg-secondary transition peer-checked:bg-primary after:absolute after:left-0.5 after:top-0.5 after:h-5 after:w-5 after:rounded-full after:bg-white after:transition peer-checked:after:translate-x-5"></span>
            </label>
            <button
              v-else
              type="button"
              class="btn btn-sm btn-outline-primary"
              :disabled="linkBusy"
              @click="linkEmailOpen = !linkEmailOpen"
            >
              Привязать
            </button>
          </div>

          <div v-if="linkEmailOpen && !hasRealEmail" class="mt-3 space-y-2">
            <input
              v-model="linkEmailValue"
              type="email"
              autocomplete="email"
              class="form-input"
              placeholder="you@example.com"
              :disabled="linkEmailSent"
            />
            <button
              v-if="!linkEmailSent"
              type="button"
              class="btn btn-sm btn-primary"
              :disabled="linkBusy"
              @click="sendLinkEmailOtp"
            >
              {{ linkBusy ? 'Отправка…' : 'Отправить код' }}
            </button>
            <template v-else>
              <input
                v-model="linkEmailCode"
                type="text"
                inputmode="numeric"
                maxlength="6"
                class="form-input font-mono"
                placeholder="Код из письма"
              />
              <div class="flex gap-2">
                <button type="button" class="btn btn-sm btn-primary" :disabled="linkBusy" @click="verifyLinkEmail">
                  Подтвердить
                </button>
                <button type="button" class="btn btn-sm btn-outline-primary" :disabled="linkBusy" @click="sendLinkEmailOtp">
                  Отправить снова
                </button>
              </div>
            </template>
          </div>
        </div>

        <!-- Telegram -->
        <div class="rounded-lg border border-white-light/60 p-3">
          <div class="flex items-center justify-between gap-3">
            <div>
              <div class="font-semibold">Вход через Telegram</div>
              <div class="text-xs text-muted">
                {{ hasTelegram ? 'Telegram привязан' : 'Telegram не привязан' }}
              </div>
            </div>
            <label v-if="hasTelegram" class="relative inline-flex cursor-pointer items-center">
              <input
                type="checkbox"
                class="peer sr-only"
                :checked="authTelegramEnabled"
                :disabled="linkBusy"
                @change="toggleAuthMethod('telegram', !authTelegramEnabled)"
              />
              <span class="h-6 w-11 rounded-full bg-secondary transition peer-checked:bg-primary after:absolute after:left-0.5 after:top-0.5 after:h-5 after:w-5 after:rounded-full after:bg-white after:transition peer-checked:after:translate-x-5"></span>
            </label>
            <button
              v-else
              type="button"
              class="btn btn-sm btn-outline-primary"
              :disabled="linkBusy"
              @click="linkTgOpen = !linkTgOpen"
            >
              Привязать
            </button>
          </div>

          <div v-if="linkTgOpen && !hasTelegram" class="mt-3 space-y-2">
            <p class="text-xs text-muted">
              Откройте бота в Telegram, получите код привязки и введите его ниже.
            </p>
            <input
              v-model="linkTgCode"
              type="text"
              class="form-input font-mono"
              placeholder="Код из Telegram"
            />
            <button type="button" class="btn btn-sm btn-primary" :disabled="linkBusy" @click="verifyLinkTelegram">
              {{ linkBusy ? 'Проверка…' : 'Подтвердить' }}
            </button>
          </div>
        </div>
      </div>

      <div class="panel space-y-4">
        <div class="flex items-center justify-between gap-3">
          <h2 class="font-bold">Двухфакторная аутентификация</h2>
          <span
            class="badge"
            :class="twoFaEnabled ? 'badge-success' : 'bg-secondary text-white'"
          >
            {{ twoFaEnabled ? 'Включена' : 'Выключена' }}
          </span>
        </div>
        <p v-if="settings?.email" class="text-sm text-muted">{{ settings.email }}</p>

        <template v-if="tfaStep === 'idle'">
          <button
            v-if="!twoFaEnabled"
            type="button"
            class="btn btn-outline-primary"
            @click="start2fa"
          >
            Включить 2FA
          </button>
          <button
            v-else
            type="button"
            class="btn btn-outline-danger"
            @click="disable2fa"
          >
            Отключить 2FA
          </button>
        </template>

        <template v-else-if="tfaStep === 'setup'">
          <p class="text-sm">Отсканируйте QR в приложении-аутентификаторе.</p>
          <button type="button" class="btn btn-sm btn-secondary" @click="qrOpen = true">
            Показать QR
          </button>
          <p v-if="tfaSecret" class="font-mono text-xs break-all text-muted">{{ tfaSecret }}</p>
          <input
            v-model="tfaCode"
            type="text"
            inputmode="numeric"
            maxlength="6"
            class="form-input font-mono"
            placeholder="Код из приложения"
          />
          <button type="button" class="btn btn-primary" @click="confirm2fa">
            Подтвердить
          </button>
        </template>

        <template v-else-if="tfaStep === 'backup'">
          <div class="alert alert-warning text-sm">
            Сохраните резервные коды — они больше не будут показаны.
          </div>
          <ul class="grid grid-cols-2 gap-2 font-mono text-sm">
            <li v-for="code in backupCodes" :key="code">{{ code }}</li>
          </ul>
          <button type="button" class="btn btn-primary" @click="tfaStep = 'idle'">
            Готово
          </button>
        </template>
      </div>

      <div class="panel space-y-4">
        <div class="flex items-center justify-between gap-3">
          <h2 class="font-bold">Кодовое слово</h2>
          <span
            class="badge"
            :class="passphraseEnabled ? 'badge-success' : 'bg-secondary text-white'"
          >
            {{ passphraseEnabled ? 'Включено' : 'Выключено' }}
          </span>
        </div>
        <p class="text-sm text-muted">
          Дополнительная защита: кодовое слово запрашивается при восстановлении доступа.
          Необязательно — включите по желанию.
        </p>

        <template v-if="passStep === 'idle'">
          <div class="flex flex-wrap gap-2">
            <button type="button" class="btn btn-outline-primary" @click="startPassphrase">
              {{ passphraseEnabled ? 'Изменить кодовое слово' : 'Включить кодовое слово' }}
            </button>
            <button
              v-if="passphraseEnabled"
              type="button"
              class="btn btn-outline-danger"
              :disabled="passBusy"
              @click="disablePassphrase"
            >
              Отключить
            </button>
          </div>
        </template>

        <template v-else>
          <input
            v-model="passValue"
            type="text"
            autocomplete="off"
            class="form-input"
            placeholder="Новое кодовое слово (мин. 4 символа)"
          />
          <input
            v-if="twoFaEnabled"
            v-model="passTotp"
            type="text"
            inputmode="numeric"
            maxlength="6"
            class="form-input font-mono"
            placeholder="Код 2FA"
          />
          <div class="flex flex-wrap gap-2">
            <button
              type="button"
              class="btn btn-primary"
              :disabled="passBusy"
              @click="savePassphrase"
            >
              {{ passBusy ? 'Сохранение…' : 'Сохранить' }}
            </button>
            <button type="button" class="btn btn-outline-primary" :disabled="passBusy" @click="passStep = 'idle'">
              Отмена
            </button>
          </div>
        </template>
      </div>

      <button type="button" class="btn btn-primary w-full" :disabled="saving" @click="save">
        {{ saving ? 'Сохранение…' : 'Сохранить настройки' }}
      </button>
    </div>

    <QrModal
      :open="qrOpen"
      :data="tfaOtpauth"
      title="2FA QR"
      @close="qrOpen = false"
    />
  </div>
</template>
